import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  TrendingUp, TrendingDown, DollarSign, PieChart, FileText, Bell, 
  User, LogOut, Loader2, Calendar, Download, Send, RefreshCw,
  Building, MapPin, Phone, Mail, Edit, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvestorDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [investor, setInvestor] = useState(null);
  const [news, setNews] = useState([]);
  const [dividends, setDividends] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [sharePrices, setSharePrices] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', email: '', phone: '', city: '' });
  const [requestData, setRequestData] = useState({ shares_requested: '', message: '' });
  const [isSaving, setIsSaving] = useState(false);

  const investorId = localStorage.getItem('investorId');

  useEffect(() => {
    if (!investorId) {
      navigate('/investors');
      return;
    }
    fetchAllData();
  }, [investorId, navigate]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [profileRes, newsRes, dividendsRes, docsRes, pricesRes, requestsRes] = await Promise.all([
        fetch(`${API_URL}/api/investor/${investorId}/profile`),
        fetch(`${API_URL}/api/news`),
        fetch(`${API_URL}/api/dividends`),
        fetch(`${API_URL}/api/documents`),
        fetch(`${API_URL}/api/share-prices`),
        fetch(`${API_URL}/api/investor/${investorId}/investment-requests`)
      ]);

      const [profileData, newsData, dividendsData, docsData, pricesData, requestsData] = await Promise.all([
        profileRes.json(),
        newsRes.json(),
        dividendsRes.json(),
        docsRes.json(),
        pricesRes.json(),
        requestsRes.json()
      ]);

      if (profileData.success) {
        setInvestor(profileData.investor);
        setProfileData({
          name: profileData.investor.name || '',
          email: profileData.investor.email || '',
          phone: profileData.investor.phone || '',
          city: profileData.investor.city || ''
        });
      }
      if (newsData.success) setNews(newsData.news);
      if (dividendsData.success) setDividends(dividendsData.dividends);
      if (docsData.success) setDocuments(docsData.documents);
      if (pricesData.success) setSharePrices(pricesData.share_prices);
      if (requestsData.success) setMyRequests(requestsData.requests);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('investorToken');
    localStorage.removeItem('investorId');
    localStorage.removeItem('userRole');
    navigate('/investors');
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/investor/${investorId}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });
      const data = await response.json();
      if (data.success) {
        setShowProfileModal(false);
        fetchAllData();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInvestmentRequest = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/investor/investment-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          investor_id: investorId,
          shares_requested: parseInt(requestData.shares_requested),
          message: requestData.message
        })
      });
      const data = await response.json();
      if (data.success) {
        setShowRequestModal(false);
        setRequestData({ shares_requested: '', message: '' });
        fetchAllData();
      }
    } catch (error) {
      console.error('Error submitting request:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const roi = investor?.roi_percentage || 0;
  const isPositiveRoi = roi >= 0;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="text-white">
                <p className="text-blue-300 text-sm">Investor Dashboard</p>
                <h1 className="text-xl md:text-2xl font-bold">{investor?.name || 'Investor'}</h1>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowProfileModal(true)} className="border-white/30 text-white hover:bg-white/10">
                <Edit className="w-4 h-4 mr-2" /> Edit Profile
              </Button>
              <Button variant="outline" onClick={handleLogout} className="border-white/30 text-white hover:bg-white/10">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <section className="py-6 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-500">Shares Held</p>
                    <p className="text-xl md:text-2xl font-bold text-gray-900">{investor?.shares_held?.toLocaleString() || 0}</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <PieChart className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-500">Amount Invested</p>
                    <p className="text-xl md:text-2xl font-bold text-gray-900">₹{investor?.amount_invested?.toLocaleString() || 0}</p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-500">Portfolio Value</p>
                    <p className="text-xl md:text-2xl font-bold text-gray-900">₹{investor?.portfolio_value?.toLocaleString() || 0}</p>
                  </div>
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Building className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-500">ROI</p>
                    <p className={`text-xl md:text-2xl font-bold ${isPositiveRoi ? 'text-green-600' : 'text-red-600'}`}>
                      {isPositiveRoi ? '+' : ''}{roi}%
                    </p>
                  </div>
                  <div className={`w-10 h-10 ${isPositiveRoi ? 'bg-green-100' : 'bg-red-100'} rounded-xl flex items-center justify-center`}>
                    {isPositiveRoi ? <TrendingUp className="w-5 h-5 text-green-600" /> : <TrendingDown className="w-5 h-5 text-red-600" />}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-6 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="flex flex-wrap gap-2">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="news">News & Updates</TabsTrigger>
              <TabsTrigger value="dividends">Dividends</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="requests">My Requests</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Investment Details */}
                <Card>
                  <CardHeader>
                    <CardTitle>Investment Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Entry Share Value</span>
                      <span className="font-semibold">₹{investor?.entry_share_value?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Discounted Share Value</span>
                      <span className="font-semibold">₹{investor?.discounted_share_value?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Current Share Value</span>
                      <span className="font-semibold text-green-600">₹{investor?.current_share_value?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500">Total Shares</span>
                      <span className="font-semibold">{investor?.shares_held?.toLocaleString() || 0}</span>
                    </div>
                    <Button onClick={() => setShowRequestModal(true)} className="w-full mt-4 bg-gradient-to-r from-blue-600 to-teal-500">
                      <Send className="w-4 h-4 mr-2" /> Request Additional Shares
                    </Button>
                  </CardContent>
                </Card>

                {/* Profile Info */}
                <Card>
                  <CardHeader>
                    <CardTitle>Profile Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3 py-2 border-b">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Name</p>
                        <p className="font-medium">{investor?.name || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 py-2 border-b">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <p className="font-medium">{investor?.email || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 py-2 border-b">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Phone</p>
                        <p className="font-medium">{investor?.phone || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 py-2">
                      <MapPin className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">City</p>
                        <p className="font-medium">{investor?.city || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Share Price History */}
              <Card>
                <CardHeader>
                  <CardTitle>Share Price History</CardTitle>
                </CardHeader>
                <CardContent>
                  {sharePrices.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-sm text-gray-500 border-b">
                            <th className="py-2">Date</th>
                            <th className="py-2">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sharePrices.slice(0, 10).map((price, index) => (
                            <tr key={price.id || index} className="border-b last:border-0">
                              <td className="py-3">{price.date}</td>
                              <td className="py-3 font-semibold">₹{price.price?.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No price history available</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* News Tab */}
            <TabsContent value="news">
              <div className="grid md:grid-cols-2 gap-6">
                {news.length > 0 ? news.map((item) => (
                  <Card key={item.id} className="overflow-hidden">
                    {item.image_url && (
                      <img src={item.image_url} alt={item.title} className="w-full h-48 object-cover" />
                    )}
                    <CardContent className="p-4">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full mb-2 ${
                        item.category === 'financial' ? 'bg-green-100 text-green-700' :
                        item.category === 'milestone' ? 'bg-blue-100 text-blue-700' :
                        item.category === 'meeting' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {item.category}
                      </span>
                      <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.content}</p>
                      <p className="text-xs text-gray-400 mt-3">{new Date(item.created_at).toLocaleDateString()}</p>
                    </CardContent>
                  </Card>
                )) : (
                  <div className="col-span-2 text-center py-12 text-gray-500">
                    <Bell className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No news or updates available</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Dividends Tab */}
            <TabsContent value="dividends">
              <Card>
                <CardHeader>
                  <CardTitle>Dividend History</CardTitle>
                </CardHeader>
                <CardContent>
                  {dividends.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-sm text-gray-500 border-b">
                            <th className="py-2">Amount/Share</th>
                            <th className="py-2">Record Date</th>
                            <th className="py-2">Payment Date</th>
                            <th className="py-2">Your Dividend</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dividends.map((div) => (
                            <tr key={div.id} className="border-b last:border-0">
                              <td className="py-3">₹{div.amount_per_share}</td>
                              <td className="py-3">{div.record_date}</td>
                              <td className="py-3">{div.payment_date}</td>
                              <td className="py-3 font-semibold text-green-600">
                                ₹{(div.amount_per_share * (investor?.shares_held || 0)).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No dividend records available</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.length > 0 ? documents.map((doc) => (
                  <Card key={doc.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold truncate">{doc.title}</h4>
                          <p className="text-sm text-gray-500 truncate">{doc.description}</p>
                          <span className="text-xs text-gray-400">{doc.document_type}</span>
                        </div>
                      </div>
                      <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="w-full mt-4" size="sm">
                          <Download className="w-4 h-4 mr-2" /> Download
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                )) : (
                  <div className="col-span-3 text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No documents available</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* My Requests Tab */}
            <TabsContent value="requests">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Investment Requests</CardTitle>
                  <Button onClick={() => setShowRequestModal(true)} size="sm">
                    <Send className="w-4 h-4 mr-2" /> New Request
                  </Button>
                </CardHeader>
                <CardContent>
                  {myRequests.length > 0 ? (
                    <div className="space-y-4">
                      {myRequests.map((req) => (
                        <div key={req.id} className="p-4 border rounded-xl">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold">{req.shares_requested} Shares Requested</p>
                              <p className="text-sm text-gray-500">{req.message || 'No message'}</p>
                              <p className="text-xs text-gray-400 mt-1">{new Date(req.created_at).toLocaleDateString()}</p>
                            </div>
                            <span className={`px-3 py-1 text-xs rounded-full ${
                              req.status === 'approved' ? 'bg-green-100 text-green-700' :
                              req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {req.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No investment requests yet</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Edit Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
            <DialogDescription>Update your contact information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={profileData.name} onChange={(e) => setProfileData({...profileData, name: e.target.value})} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={profileData.email} onChange={(e) => setProfileData({...profileData, email: e.target.value})} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} />
            </div>
            <div>
              <Label>City</Label>
              <Input value={profileData.city} onChange={(e) => setProfileData({...profileData, city: e.target.value})} />
            </div>
            <Button type="submit" className="w-full" disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Request Shares Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Additional Shares</DialogTitle>
            <DialogDescription>Submit a request to purchase more shares</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvestmentRequest} className="space-y-4">
            <div>
              <Label>Number of Shares</Label>
              <Input 
                type="number" 
                min="1"
                value={requestData.shares_requested} 
                onChange={(e) => setRequestData({...requestData, shares_requested: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Message (Optional)</Label>
              <Textarea 
                value={requestData.message} 
                onChange={(e) => setRequestData({...requestData, message: e.target.value})}
                placeholder="Any additional information..."
              />
            </div>
            <Button type="submit" className="w-full" disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Submit Request
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvestorDashboard;
